/**
 * One place for how toasts behave, so main.tsx stays a mount point.
 *
 *  - Native app: ONE visible toast, centred under the app bar. Three stacked
 *    error cards covered the top of whatever sheet was open (a chairman's
 *    student sheet, Record Payment) and hid its header and close button.
 *  - Identical messages collapse into one toast. A screen that fires the same
 *    failing request from three components used to paint three copies.
 */
import { toast } from "sonner";
import { noteAppError } from "./app-review";

type ToastData = { id?: string | number } & Record<string, unknown>;
type MessageFn = (message: unknown, data?: ToastData) => string | number;

/** The kinds whose first argument is a plain message. `custom`, `promise`, `loading` are left alone. */
const DEDUPED_KINDS = ["success", "info", "warning", "error", "message"] as const;

let installed = false;

/**
 * Give every string-message toast a stable id (`kind:message`) when the caller
 * didn't pass one. Sonner treats a repeated id as an UPDATE of the toast on
 * screen, which is exactly the de-duplication wanted. Safe to call twice.
 * Messages that carry their own `id` (a "saving…" toast that is later replaced)
 * are never touched.
 */
export function installToastDedupe(target: typeof toast = toast): void {
  if (installed) return;
  installed = true;
  const bag = target as unknown as Record<string, MessageFn>;
  for (const kind of DEDUPED_KINDS) {
    const original = bag[kind];
    if (typeof original !== "function") continue;
    bag[kind] = (message, data) => {
      // The store-rating prompt stays quiet for two minutes after an error toast.
      if (kind === "error") noteAppError();
      if (typeof message !== "string" || data?.id !== undefined) return original(message, data);
      return original(message, { ...data, id: `${kind}:${message}` });
    };
  }
}

/** Test seam: forget that the dedupe was installed. */
export function resetToastDedupeForTests(): void {
  installed = false;
}

/** App bar (52px; the dashboard hero bar is ~74px) + its safe-area inset + a few px of air. */
export const NATIVE_TOAST_TOP = "calc(env(safe-area-inset-top, 0px) + 76px)";

export interface ToasterLayout {
  position: "top-right" | "top-center";
  visibleToasts: number;
  offset: { top: string; right: string; left: string; bottom: string } | undefined;
  mobileOffset: { top: string; right: string; left: string; bottom: string } | undefined;
}

/** Layout props for `<Toaster />`. `appLike` = native shell / installed PWA / forced app view. */
export function toasterLayout(appLike: boolean): ToasterLayout {
  if (!appLike) {
    return { position: "top-right", visibleToasts: 3, offset: undefined, mobileOffset: undefined };
  }
  const inset = { top: NATIVE_TOAST_TOP, right: "12px", left: "12px", bottom: "12px" };
  return { position: "top-center", visibleToasts: 1, offset: inset, mobileOffset: inset };
}
